import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import { DoorList } from "../marketing/DoorList.tsx";
import { ReviewQueue } from "../marketing/ReviewQueue.tsx";
import { Drops } from "../marketing/Drops.tsx";
import { CampaignDashboard } from "../marketing/CampaignDashboard.tsx";
import contentLibrary from "../marketing/ContentLibrary.tsx";
import sequenceLibrary from "../marketing/Sequences.tsx";
import { AllTasksScreen, MobileAllTasks } from "../marketing/AllTasks";
import { marketingMobileRoutes } from "../marketing/mobile.tsx";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
  /**
   * Barrel Scout injection: lets a caller supply its own react-query
   * QueryClient instead of always getting the one this component builds --
   * e.g. StoryWrapper.tsx passes one with `retry: false` so a browser test
   * asserting an error state does not pay react-query's default 3-attempt
   * backoff (each attempt ~7s). Absent, DesktopAdmin and MobileAdmin build
   * EXACTLY the client they always have -- this prop is opt-in and changes
   * nothing for a caller that does not pass one. Guarded by
   * marketing/check-injections.mjs.
   */
  queryClient?: QueryClient;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI — never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={StartPage}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
      </CustomRoutes>

      <CustomRoutes>
        {/* Barrel Scout marketing screens, on DESKTOP. The same routes are
            registered in the mobile <Admin> below, so both surfaces use the
            same /marketing/* paths and the nav links cannot drift from the
            routes. Guarded by marketing/check-injections.mjs. */}
        {marketingMobileRoutes}
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      {/* Barrel Scout injection: DESKTOP HAD NO TASK LIST AT ALL. This was a
          bare <Resource name="tasks" />, so #/tasks resolved to nothing on a
          laptop while the phone's bottom bar opened a screen at the same URL
          -- and a task closed more than five minutes ago was invisible
          everywhere in the app (atomic#15). Guarded by
          marketing/check-injections.mjs; reached from the dashboard's
          Upcoming Tasks card, since this header hardcodes its four tabs. */}
      <Resource name="tasks" list={AllTasksScreen} />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
      {/* The mailing universe. Read-only from the CRM: doors come from the
          TTB pipeline via sync_to_db.py, and anything created here would be
          overwritten by the next sync. */}
      <Resource
        name="door_directory"
        list={DoorList}
        options={{ label: "Doors" }}
      />
      {/* Read by MailHistory for the door behind a promoted company, and
          written to by the Doors list when recording an opt-out. */}
      <Resource name="addresses" />
      <Resource name="suppressions" />
      {/* Triage. Candidates come from the pipeline via sync_review_queue.py;
          this only records the verdict. */}
      <Resource
        name="review_backlog"
        list={ReviewQueue}
        options={{ label: "Review" }}
      />
      {/* Not surfaced in the menu: written to by ReviewQueue, read by the
          pipeline. Registered so the data provider can reach it. */}
      <Resource name="decisions" />
      {/* Schedule, plan and send a drop. Sending goes through the send-drop
          edge function, which holds the printer credentials -- the browser can
          ask for a send but never holds the key that performs one. */}
      <Resource
        name="drop_manifest"
        list={Drops}
        options={{ label: "Drops" }}
      />
      {/* Read by the Drops board and written by scheduling. Not surfaced in the
          menu: they are reached through the Drops screen, not on their own. */}
      <Resource name="drop_board" />
      {/* The Materials board reads readiness through the view rather than
          recomputing it: creative_readiness asks the question send-drop asks,
          and 085 exists because those two used to disagree. */}
      <Resource name="creative_readiness" />
      {/* The library PCM holds (086). Read by the Materials picker; keyed on
          PCM's own design_id, so it needs its entry in NON_ID_PRIMARY_KEYS. */}
      <Resource name="pcm_designs" />
      <Resource name="drop_schedule" />
      <Resource name="send_queue" />
      <Resource name="mailings" />
      {/* Campaign readout, including the mailed-vs-holdout comparison that is
          the only honest measure of whether the postcards did anything. */}
      <Resource
        name="campaign_by_cell"
        list={CampaignDashboard}
        options={{ label: "Campaign" }}
      />
      <Resource name="holdout_comparison" />
      {/* Reusable email / call-script / LinkedIn / note bodies that sequence
          steps send by name. Retire via is_active, never delete: sequence
          steps and engagement history keep pointing at a row long after it
          stops being current. */}
      <Resource
        name="content_assets"
        {...contentLibrary}
        options={{ label: "Content" }}
      />
      {/* Named cadences of follow-up steps. Registered for the CREATE route
          it brings -- /sequences/create -- which is where the New sequence
          button on /contacts/sequences goes; the list and the step editor are
          custom routes under /contacts so they sit inside the Contacts
          sub-navigation. Retire via is_active, never delete: enrollments
          reference a sequence and deleting one destroys the record of what
          somebody was actually enrolled in. */}
      <Resource
        name="sequences"
        {...sequenceLibrary}
        options={{ label: "Sequences" }}
      />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  // Barrel Scout injection: honor a caller-supplied QueryClient (see the
  // `queryClient` prop on <CRM>) instead of unconditionally building a new
  // one on every render. Absent, this constructs EXACTLY the client that
  // existed here before this injection -- same gcTime, same networkMode --
  // so upstream behavior is unchanged for every caller that does not pass
  // one. Guarded by marketing/check-injections.mjs.
  const queryClient =
    props.queryClient ??
    new QueryClient({
      defaultOptions: {
        queries: {
          gcTime: 1000 * 60 * 60 * 24, // 24 hours
          networkMode: "offlineFirst",
        },
        mutations: {
          networkMode: "offlineFirst",
        },
      },
    });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          {/* Barrel Scout marketing screens. Guarded by
              marketing/injection.test.ts -- if a merge drops this line the
              test fails rather than the app silently losing them. */}
          {marketingMobileRoutes}
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource name="companies" show={CompanyShow} />
        {/* Barrel Scout injection: the same screen as desktop, in the mobile
            chrome -- upstream's MobileTasksList shows open work only, so a
            phone could tick a task off and never see it again. Guarded by
            marketing/check-injections.mjs. */}
        <Resource name="tasks" list={MobileAllTasks} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
